// TaskRectangle — the bread-and-butter primitive of the cooking screen.
// Renders a task in one of four cookpit-aligned states (matching the
// classifier output in Phase 3) plus an independent `livecookExpired`
// boolean that overlays the damped-red treatment from §7.1.
//
// The Phase 3 classifier emits `upcoming · active · overrun · done`.
// The mapping is the consumer's responsibility, documented here:
//   overrun → incompletePast
//   done    → completed
//
// See README §6.7.3 (active/upcoming layout, chef-hat affordance,
// checkbox) and §6.7.4 (colour states — Option B column).

import { type ReactNode } from 'react';
import { Chip } from './Chip';
import { Checkbox } from './Checkbox';
import { ChefHat } from './ChefHat';
import { AssistText, type TaskAssistMatcher } from './AssistText';
import { laneLabel, laneTone } from './laneLabel';
import { theme } from '../theme';

export type TaskRectangleState = 'upcoming' | 'active' | 'incompletePast' | 'completed';

// Re-exported so existing importers (ActiveTaskCard, PhasePane) keep
// resolving the matcher type from here; it now lives in AssistText.
export type { TaskAssistMatcher } from './AssistText';

export interface TaskRectangleProps {
  state: TaskRectangleState;
  /** Independent of `state`: applies the damped-red LiveCook-expired tint. */
  livecookExpired?: boolean;
  /**
   * Set on rectangles rendered in the look-back band when the task is
   * currently classified as `overrun` — surfaces a black edge so the
   * user can see at a glance that the item is still outstanding (and
   * mirrors the black banner at the top of the pane). Server-side
   * classification is unaffected; this is presentation only.
   */
  overrunHistory?: boolean;
  title: string;
  /** HH:MM scheduled start, e.g. "13:08". Display only. */
  time?: string;
  /** HH:MM scheduled finish, e.g. "13:23". When supplied alongside
   *  `time` the row reads "<start> / <finish>" (README LiveCook brief). */
  endTime?: string;
  /** ISO instant for the task's scheduled start. Used for keyboard-nav
   * time-order assertions and any future tooling that needs a sortable
   * value (HH:MM does not sort across day boundaries). */
  startAtIso?: string;
  /** Lane chip text, e.g. "A0", "S1". */
  lane?: string;
  /** Render the chef-hat affordance opposite the checkbox. */
  chefHatRef?: boolean;
  /**
   * Which edge of the rectangle holds the checkbox. The chef-hat
   * affordance moves with it so the rectangle stays mirror-symmetric.
   * Default 'left' for backwards compatibility.
   */
  checkboxSide?: 'left' | 'right';
  /**
   * Whether to render the completion checkbox. Default true. The cooking
   * pane sets this false on inline `upcoming` tasks so the checkbox is
   * only present once a task is live (paired with its countdown clock);
   * overrun catch-up then happens through the Outstanding panel. A 44×44
   * placeholder keeps the title column aligned when the box is hidden.
   */
  showCheckbox?: boolean;
  /**
   * Render the checkbox in its greyed, non-interactive `disabled` state.
   * Used by the dormant "next" card during an early-finish gap: the task
   * is elevated but not yet startable, so its box is greyed until its
   * authored time arrives. Ignored when `state === 'completed'`.
   */
  checkboxDisabled?: boolean;
  /**
   * When true, applies the slide-up + fade exit animation via a CSS
   * class. Consumers (the cooking panes) drive this with the
   * `useSlideOutOnComplete` hook so that the rectangle stays in the
   * tree for ~300ms after the classifier flips it to `completed`.
   */
  slidingOut?: boolean;
  /**
   * Optional element rendered on the trailing edge of the rectangle,
   * before the chef-hat. Used by `ActiveTaskCard` to host the countdown
   * dial while a task is active (README §6.7.3 item 3). It mirrors with
   * `checkboxSide` like the rest of the row.
   */
  trailingAccessory?: ReactNode;
  /**
   * Optional element rendered on the leading edge of the rectangle,
   * immediately before the checkbox. Used by `ActiveTaskCard` to host the
   * countdown clock to the left of the check box for active tasks. It
   * mirrors with `checkboxSide` like the rest of the row.
   */
  leadingAccessory?: ReactNode;
  /**
   * When supplied, words in the title that resolve through the matcher are
   * rendered as Kitchen Assist help links; clicking one calls `onAssistWord`
   * with the canonical (folded) word. Omitted ⇒ the title is plain text —
   * the cooking pane withholds it from completed / look-back cards so help
   * shows only on active and forthcoming tasks (README §12.2).
   */
  assistMatcher?: TaskAssistMatcher;
  onAssistWord?: (canonicalWord: string) => void;
  onCheck?: () => void;
  onChefHatClick?: () => void;
}

interface VisualState {
  bg: string;
  fg: string;
  halo?: boolean;
  dot?: boolean;
  tick?: boolean;
}

function visualFor(state: TaskRectangleState): VisualState {
  switch (state) {
    case 'upcoming':
      return { bg: theme.bg.raised, fg: theme.text.primary };
    case 'active':
      return { bg: theme.bg.base, fg: theme.text.primary, halo: true };
    case 'incompletePast':
      return { bg: theme.bg.muted, fg: theme.text.primary, dot: true };
    case 'completed':
      return { bg: theme.accent.calm, fg: theme.text.primary, tick: true };
  }
}

export function TaskRectangle({
  state,
  livecookExpired = false,
  overrunHistory = false,
  title,
  time,
  endTime,
  startAtIso,
  lane,
  chefHatRef = false,
  checkboxSide = 'left',
  showCheckbox = true,
  checkboxDisabled = false,
  slidingOut = false,
  trailingAccessory,
  leadingAccessory,
  assistMatcher,
  onAssistWord,
  onCheck,
  onChefHatClick,
}: TaskRectangleProps) {
  const v = visualFor(state);
  // `livecookExpired` overlays the damped-red wash from §7.1 on top of
  // the otherwise-normal state. The state's own bg fades to the late
  // accent; text stays charcoal for contrast.
  const bg = livecookExpired ? theme.accent.late : v.bg;
  const fg = livecookExpired ? theme.surface.onAccent : v.fg;
  const checkboxState =
    state === 'completed' ? 'checked' : checkboxDisabled ? 'disabled' : 'unchecked';
  // Active task panels carry a hairline edge (under the sage halo) so the
  // live card reads as a defined, elevated panel against the page (README
  // §6.7.3). Overrun-history's black edge still wins when set.
  const border = overrunHistory
    ? `2px solid ${theme.overrunBanner.bg}`
    : state === 'active'
      ? `1px solid ${theme.bg.muted}`
      : '1px solid transparent';
  const className = slidingOut ? 'cc-task-rect cc-task-slide-out' : 'cc-task-rect';
  // "<start> / <finish>" when both are present; just the start otherwise.
  const timeText = time && endTime ? `${time} / ${endTime}` : time;
  const timeLabel = time && endTime ? `${time} to ${endTime}` : time;
  return (
    <div
      role="group"
      aria-label={`${title}${timeLabel ? `, ${timeLabel}` : ''}, ${state}${livecookExpired ? ', LiveCook expired' : ''}${overrunHistory ? ', outstanding' : ''}`}
      data-start-at={startAtIso ?? time}
      data-overrun-history={overrunHistory ? '' : undefined}
      data-checkbox-side={checkboxSide}
      data-sliding-out={slidingOut ? '' : undefined}
      style={{
        display: 'flex',
        flexDirection: checkboxSide === 'right' ? 'row-reverse' : 'row',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.75rem 1rem',
        borderRadius: '10px',
        background: bg,
        color: fg,
        border,
        boxShadow: v.halo ? `0 0 0 4px ${theme.accent.calm}33` : 'none',
        minHeight: '64px',
        transition: `background ${theme.motion.duration.medium} ${theme.motion.easing}`,
      }}
      className={className}
    >
      {leadingAccessory}
      {showCheckbox ? (
        <Checkbox state={checkboxState} label={`Mark "${title}" complete`} onChange={onCheck} />
      ) : (
        <span aria-hidden="true" style={{ width: '44px', height: '44px', flex: '0 0 auto' }} />
      )}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {v.dot && (
            <span
              aria-hidden="true"
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: theme.accent.action,
                display: 'inline-block',
                flex: '0 0 auto',
              }}
            />
          )}
          <span style={{ fontSize: '1rem', fontWeight: 600 }}>
            <AssistText text={title} matcher={assistMatcher} onAssistWord={onAssistWord} />
          </span>
        </div>
        {(timeText || lane) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {timeText && (
              <span
                style={{
                  fontSize: '0.85rem',
                  color: livecookExpired ? theme.surface.onAccent : theme.text.muted,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {timeText}
              </span>
            )}
            {lane && <Chip text={laneLabel(lane)} variant="lane" tone={laneTone(lane)} />}
          </div>
        )}
      </div>
      {trailingAccessory}
      {chefHatRef && (
        <ChefHat ariaLabel={`Notes for ${title}`} onClick={onChefHatClick ?? (() => undefined)} />
      )}
    </div>
  );
}

export default TaskRectangle;
